import { useEffect, useRef, useState } from "react";
import type {
  BuyerResearchRefinementStateDto,
  BuyerResearchRunSummaryDto,
  BuyerResearchSourceLogEntryDto,
} from "@/api/buyerResearch";
import { pullRefusalBlocker } from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import { Button } from "@/design-system/components/ui";
import { researchCopy } from "./researchCopy";
import { formatCost, scoringProgressOf } from "./researchFormat";
import { useElapsedLabel, useObservedSince, usePullMoreResearch } from "./useBuyerResearch";

/**
 * "Pull more" — buy the next page of this run's own search, rank it, and append
 * it beneath what is already on screen.
 *
 * <p>🔴 <b>THE CLIENT DECIDES NOTHING (RC2).</b> Whether a pull is available,
 * what it would cost, what is left of the budget and why it is closed all arrive
 * on <c>run.pull</c>, computed by the one expression the endpoint's own
 * pre-flight runs. This component renders a boolean and a sentence. It does no
 * page arithmetic, subtracts no budget and guesses at no page size — a second
 * predicate here would be free to offer a pull the server then refuses, or to
 * hide one it would have accepted.</p>
 *
 * <p>🔴 <b>RC14 — CLEF-SPECIFIC, and the gate is a non-null
 * <c>clefMatchCount</c>.</b> Paging is a fact about Clef's Enterprise feed;
 * nothing else this run can search has pages to buy. That column is null exactly
 * when no Clef source ran, so on a web-sources-only run the control is ABSENT
 * rather than disabled — which, with the web sources off by default, is the
 * shape most runs would otherwise take: a permanently greyed button explaining
 * an absence nobody asked about. RC2 still governs what the control SAYS once it
 * is present; RC14 governs whether it is present at all.</p>
 */
export function ResearchPullControl({
  buyerLeadId,
  run,
  sourceLog,
  canEdit,
  refinement = null,
  onPullStarted,
}: {
  buyerLeadId: string;
  run: BuyerResearchRunSummaryDto;
  /** The run's whole source log, which is where the last pull's figures live. */
  sourceLog: BuyerResearchSourceLogEntryDto[];
  canEdit: boolean;
  /**
   * 🔴 <b>DECISION 6 — PULL MORE CONTINUES THE REFINEMENT.</b> With one
   * applied, the next page is a page of the REFINEMENT's search rather than the
   * brief's, and the offer has to say so: a specialist deciding whether to
   * spend records is deciding what they are spending them on. Clearing the
   * refinement restores the original search as the pull target, at which point
   * the ordinary sentence is true again.
   *
   * <p>It changes the SENTENCE and nothing else. Whether a pull is available,
   * what it costs and what is left of the budget still arrive on
   * <c>run.pull</c> — RC2's rule, which a refinement does not suspend.</p>
   */
  refinement?: BuyerResearchRefinementStateDto | null;
  /**
   * RG23 — the pull was queued. The owner puts the list back on page 1, once:
   * the new batch's rows arrive a group at a time and the reader should meet
   * the first of them. Nothing resets the page after this, so a reader who
   * pages on while the batch is still being scored stays where they went.
   */
  onPullStarted?: () => void;
}) {
  const copy = researchCopy.pull;
  const pullMore = usePullMoreResearch(buyerLeadId);
  const [error, setError] = useState<string | null>(null);

  // 🔴 R9 — A PULL IS QUEUED, AND THE WORKER BUYS. The request answers at once
  // with the run in `PullQueued`; the worker moves it to `Pulling` and back to a
  // terminal status. Both are WORK IN PROGRESS on this control, shown the way a
  // first run's bar shows its own: what is happening, and for how long.
  const working = run.status === "PullQueued" || run.status === "Pulling";
  // Measured from when THIS PAGE first saw the pull working — the wire carries
  // no start time for a queued pull — so the copy says "so far".
  const elapsed = useElapsedLabel(useObservedSince(working), working);

  // Where the source log ended when this page first saw the pull working. What
  // the pull wrote lands AFTER it, and that is the only way to recognise a
  // pull that never started: its skip line bought no batch, so it carries the
  // default batch 1, and `lastPullEntry` cannot tell it from the run's own
  // search. Reset at the start of each pull, and never guessed on a page that
  // did not watch one — that page falls back to the last pull's line.
  const [logLengthAtStart, setLogLengthAtStart] = useState<number | null>(null);
  const wasWorking = useRef(false);
  useEffect(() => {
    if (working && !wasWorking.current) setLogLengthAtStart(sourceLog.length);
    wasWorking.current = working;
  }, [working, sourceLog.length]);

  const pull = run.pull;
  if (pull == null || run.clefMatchCount == null) return null;

  const observed =
    !working && logLengthAtStart != null ? sourceLog.slice(logLengthAtStart) : [];
  const result = working
    ? null
    : (observed.length > 0 ? observed[observed.length - 1] : null) ?? lastPullEntry(sourceLog);

  // Applied, not merely present: a refinement that was cleared, refused or is
  // still classifying has not moved the pull target, and captioning the offer
  // with one that had not taken effect would be the same false sentence in the
  // other direction.
  const refined =
    refinement?.active != null && refinement.active.status === "applied";

  // No `border-t` on this section, and none on any sibling. The selected run
  // has SIX siblings — brief, query transcript, sources, Ask Phoenix, results,
  // pull — and THREE drew a rule above themselves: the transcript, Ask Phoenix
  // and this one. So the rule marked no boundary a reader could name; it marked
  // which components happened to have been written with one. 🔴 And the three
  // do not even share a shape: two of them render their own heading and the
  // pull control renders none, so the rule was not tracking "a titled section
  // starts here" either. §17.5 calls that a structural signal corresponding to
  // no structure; §17.2's heading ladder carries the division and the parent's
  // `gap-space-4` carries the spacing.
  //
  // ⚠️ The count is SIX, not the five the §17.4 note in `ResearchTab` cites:
  // that note is counting sections that render a HEADING, which the pull
  // control does not. Re-derived rather than copied.
  return (
    <section
      className="grid gap-space-2"
      data-testid="research-pull"
      data-can-pull={pull.canPull}
    >
      {/* One live region for the pull's progress AND its result, so the change
          from one to the other is announced. It persists across that change
          because it is the same element; it is absent only when there is
          neither, so it adds no empty row to the grid. */}
      {(working || result) && (
        <div role="status" className="grid gap-space-1" data-testid="research-pull-status">
          {working ? (
            <p
              className="m-0 text-caption text-gray-mid break-words"
              data-testid="research-pull-progress"
              data-status={run.status}
            >
              {pullProgressSentence(run)}
              {elapsed && (
                <span data-testid="research-pull-elapsed"> {copy.elapsed(elapsed)}</span>
              )}
            </p>
          ) : (
            result && <PullResult entry={result} />
          )}
        </div>
      )}

      {error && (
        <p className="m-0 text-caption text-status-error" role="alert" data-testid="research-pull-error">
          {error}
        </p>
      )}

      {/* While the pull works there is nothing to offer and nothing to explain:
          the server's own answer would be "run busy", which the progress line
          above already says in more detail. */}
      {working ? null : pull.canPull ? (
        <div className="flex flex-wrap items-center gap-space-2">
          {canEdit && (
            <Button
              size="sm"
              variant="secondary"
              loading={pullMore.isPending}
              onClick={() => {
                setError(null);
                pullMore.mutate(run.id, {
                  onSuccess: () => onPullStarted?.(),
                  onError: (e: unknown) => setError(refusalMessage(e, pull.reserveUsd)),
                });
              }}
              data-testid="research-pull-more"
            >
              {pullMore.isPending ? copy.actionPending : copy.action}
            </Button>
          )}
          <span className="text-caption text-gray-mid break-words" data-testid="research-pull-available">
            {refined
              ? copy.availableRefined(pull.recordsPerPull)
              : copy.available(pull.recordsPerPull)}{" "}
            {copy.budgetLeft(formatCost(pull.remainingBudgetUsd))}
          </span>
        </div>
      ) : (
        <p className="m-0 text-caption text-gray-mid break-words" data-testid="research-pull-blocked">
          {blockerSentence(pull.blocker, pull.reserveUsd)}
          {/* RF7's second clause, as a SEPARATE sentence appended after the
              caption rather than folded into it.

              🔴 SEPARATE BECAUSE THE MAP ABOVE IS A STATIC `Record` AND THE
              WINDOW IS CONFIGURATION. `ClefOptions.MinReRunHours` defaults to
              24 and a slot may set anything, so a hardcoded "24 hours" inside
              the map would be redundant on the default and wrong the moment it
              moved — which is exactly why `ClefListingSource.ReRunRemedy`
              declines to restate it. Nothing here hardcodes 24.

              ⚠️ NULL OR ABSENT MEANS THE SERVER DID NOT SAY, and the clause is
              then OMITTED rather than guessed. A window printed as "0 hours",
              or printed as 24 on a slot configured otherwise, is worse than a
              sentence that does not mention one. */}
          {RERUN_RELEVANT_BLOCKERS.has(pull.blocker ?? "") &&
            typeof pull.reRunWindowHours === "number" &&
            pull.reRunWindowHours > 0 && (
            <span data-testid="research-pull-rerun-window">
              {" "}
              {copy.reRunWindow(pull.reRunWindowHours)}
            </span>
          )}
        </p>
      )}
    </section>
  );
}

/**
 * 🔴 THE BLOCKERS WHOSE REMEDY IS RUNNING THE BRIEF AGAIN — and the only ones
 * the re-run window clause belongs on.
 *
 * <p>The server sends `reRunWindowHours` whenever it knows the window, which is
 * nearly always; it does NOT decide relevance, and its own comment says that
 * stays the client's call. Appending the clause to every blocker made the
 * client decline to make it: seven of the nine captions became non-sequiturs
 * — <i>"This run is still working. The re-run window is 24 hours from the last
 * search."</i> — and `spend cap reached`, whose sentence already says an
 * unchanged brief is refused inside the window, said it twice.</p>
 *
 * <p>These two tell the specialist to run the brief again — `clef page unknown`
 * in as many words, `spend cap reached` because a fresh run is what buys a
 * fresh budget — so for them the window is the next thing they need to know,
 * and the clause supplies the number their own sentence references. A caption
 * that volunteers an irrelevant fact costs more than the fact is worth: it
 * teaches the reader to skim the ones that matter.</p>
 */
const RERUN_RELEVANT_BLOCKERS = new Set<string>(["clef page unknown", "spend cap reached"]);

/**
 * What the control says while the pull works, in the order the worker does it:
 * waiting for a worker, then buying, then scoring the bought rows a group of
 * twenty at a time (RG23). The scoring sentence is the tab's own, shared with
 * a first run, so the two never describe the same work in two ways.
 */
function pullProgressSentence(run: BuyerResearchRunSummaryDto): string {
  if (run.status === "PullQueued") return researchCopy.pull.queued;
  const scoring = scoringProgressOf(run);
  return scoring
    ? researchCopy.tab.scoringProgress(scoring.done, scoring.planned)
    : researchCopy.pull.pulling;
}

/**
 * The last pull's log entry, or null on a run that has never been pulled.
 *
 * <p>🔴 <b>POSITIONAL — the LAST entry above batch 1, never the one with the
 * highest batch number.</b> A batch number is minted as
 * <c>MAX(candidate.BatchNumber) + 1</c>, so a page that yielded no new
 * candidates at all — every row a duplicate, which offset paging over a moving
 * feed produces routinely — leaves the number where it was, and the next pull
 * reuses it. Two entries can therefore share a batch number, and a max would
 * pick whichever the sort happened to reach first. The log is appended to and
 * serialised as an array, so position is the reliable answer to "which one
 * happened last".</p>
 *
 * <p>Batch 1 is the run's own search and is excluded: its figures are the run's
 * header, and reporting them here would caption an offer to pull with what the
 * original run cost.</p>
 */
function lastPullEntry(
  sourceLog: BuyerResearchSourceLogEntryDto[],
): BuyerResearchSourceLogEntryDto | null {
  for (let i = sourceLog.length - 1; i >= 0; i--) {
    const entry = sourceLog[i];
    if (entry.batchNumber > 1) return entry;
  }
  return null;
}

/**
 * What the pull this page last saw did: the batch line, or why there is none.
 *
 * <p>Keyed on the OUTCOME the server wrote, never on the reason text:</p>
 * <ul>
 * <li><b>skipped</b> — the queued pull did not start (or its source declined),
 * so nothing was bought. The server's own sentence says why.</li>
 * <li><b>failed</b> — the pull broke part-way. The server's sentence, and the
 * records Clef delivered before it broke, which were still charged (RC8).</li>
 * <li>anything else — the batch line: what it cost, and any rows it bought and
 * could not score (R1/R2).</li>
 * </ul>
 */
function PullResult({ entry }: { entry: BuyerResearchSourceLogEntryDto }) {
  const copy = researchCopy.pull;
  const said = entry.error?.trim() ? entry.error.trim() : null;

  if (entry.outcome === "skipped") {
    return (
      <p className="m-0 text-caption text-gray-mid break-words" data-testid="research-pull-not-started">
        {said ?? copy.notStartedFallback}
      </p>
    );
  }

  if (entry.outcome === "failed") {
    return (
      <p className="m-0 text-caption text-status-error break-words" data-testid="research-pull-failed">
        {said ?? copy.failedFallback}
        {entry.recordsUsed > 0 && <> {copy.failedCharged(entry.recordsUsed)}</>}
      </p>
    );
  }

  // RC8 — what the last pull COST. Plain caption, deliberately NOT the
  // divider's uppercase treatment: RC13's classes are for a SECTION LABEL, and
  // setting a full sentence in letter-spaced capitals makes it a banner rather
  // than a figure.
  return (
    <p className="m-0 text-caption text-gray-mid break-words" data-testid="research-pull-last-batch">
      {copy.lastBatch(
        entry.recordsUsed,
        entry.duplicatesDropped,
        entry.alreadyDismissed,
        entry.unscored ?? 0,
        entry.unscoredReason ?? null,
      )}
    </p>
  );
}

/**
 * The sentence for a blocker code.
 *
 * <p>A code this client has not learned yet falls back to a readable sentence
 * rather than the slug, and never to nothing — a closed control with no
 * explanation is worse than one whose explanation is general.</p>
 *
 * <p>R2 — `ai budget reserved` names the figure kept for Ask Phoenix when the
 * server sent one, and the figure-free form when it did not. A figure the client
 * made up would be a claim about this run's budget nobody measured.</p>
 */
function blockerSentence(blocker: string | null, reserveUsd?: number | null): string {
  const copy = researchCopy.pull;
  if (blocker === "ai budget reserved" && typeof reserveUsd === "number" && reserveUsd > 0) {
    return copy.aiBudgetReserved(formatCost(reserveUsd));
  }
  if (blocker != null && blocker in copy.blocked) {
    return copy.blocked[blocker as keyof typeof copy.blocked];
  }
  return copy.blockedFallback;
}

/**
 * 🔴 A refused pull is a 400 carrying a blocker CODE in `details.code` — read it
 * with `pullRefusalBlocker`, never from the message, so the surface renders its
 * own copy and a reworded server message cannot silently stop matching.
 *
 * <p>Anything else — a network failure, a 404, a 500 — is not a blocker and
 * falls through to the ordinary message, which is the honest thing to show for a
 * failure that is not a decision.</p>
 */
function refusalMessage(e: unknown, reserveUsd?: number | null): string {
  const blocker = pullRefusalBlocker(e);
  return blocker != null ? blockerSentence(blocker, reserveUsd) : extractErrorMessage(e);
}
